// src/pages/Communications.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  orderBy,
  getDoc,
  documentId
} from 'firebase/firestore';

const Communications = ({ currentUser }) => {
  // State Management
  const [activeTab, setActiveTab] = useState('lists');
  const [savedLists, setSavedLists] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // View Members State
  const [viewingMembers, setViewingMembers] = useState(null);
  const [listMembers, setListMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  
  // Default reply-to email from tenant config
  const [defaultReplyTo, setDefaultReplyTo] = useState('');
  
  // Compose Message State
  const [messageForm, setMessageForm] = useState({
    subject: '',
    body: '',
    type: 'email', // email, sms, both
    scheduledFor: null,
    selectedListId: null,
    replyTo: '' // Reply-to email field
  });

  // Edit List Modal
  const [editingList, setEditingList] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Message Templates
  const [templates, setTemplates] = useState([
    { id: 1, name: 'Event Invitation', subject: 'You\'re Invited: {{eventName}}', body: 'Dear {{firstName}},\n\nWe would love to have you join us for {{eventName}} on {{eventDate}}.\n\nLocation: {{eventLocation}}\nTime: {{eventTime}}\n\nPlease RSVP by replying to this message.\n\nThank you for your service!\n\nBest regards,\n{{senderName}}' },
    { id: 2, name: 'Volunteer Reminder', subject: 'Reminder: {{eventName}} Tomorrow', body: 'Hi {{firstName}},\n\nThis is a friendly reminder that you\'re scheduled to volunteer at {{eventName}} tomorrow.\n\nTime: {{eventTime}}\nLocation: {{eventLocation}}\n\nIf you need to cancel, please let us know ASAP.\n\nSee you there!\n{{senderName}}' },
    { id: 3, name: 'Thank You', subject: 'Thank You for Volunteering!', body: 'Dear {{firstName}},\n\nThank you so much for volunteering at {{eventName}}. Your dedication and hard work made a real difference!\n\nWe logged {{hoursServed}} hours for this event.\n\nWe hope to see you at our next event!\n\nWith gratitude,\n{{senderName}}' }
  ]);

  useEffect(() => {
    if (currentUser?.tenantId) {
      loadSavedLists();
      loadCampaigns();
      loadDefaultReplyTo();
    }
  }, [currentUser]);

  // Load default reply-to email from tenant configuration
  const loadDefaultReplyTo = async () => {
    if (!currentUser?.tenantId) return;
    
    try {
      // Load the tenant's email configuration
      const emailConfigPath = `${currentUser.tenantId}_email_system`;
      const emailConfigDoc = await getDoc(
        doc(db, 'tenantConfigurations', emailConfigPath)
      );
      
      if (emailConfigDoc.exists()) {
        const configData = emailConfigDoc.data();
        // Try different paths where the email might be stored
        const volunteerEmail = configData?.recipients?.volunteerApplications || 
                             configData?.configValue?.recipients?.volunteerApplications ||
                             '';
        
        if (volunteerEmail) {
          console.log('Loaded default reply-to email:', volunteerEmail);
          setDefaultReplyTo(volunteerEmail);
          // Set as default in form
          setMessageForm(prev => ({
            ...prev,
            replyTo: volunteerEmail
          }));
        }
      }
    } catch (error) {
      console.log('Could not load email configuration:', error);
    }
  };

  // Load saved volunteer lists
  const loadSavedLists = async () => {
    try {
      console.log('Loading saved lists...');
      console.log('Current user:', currentUser);
      console.log('Tenant ID:', currentUser?.tenantId);
      
      if (!currentUser?.tenantId) {
        console.error('No tenantId found');
        setError('Unable to load lists - missing tenant information');
        return;
      }
      
      // Simple query without orderBy first to avoid index issues
      const listsQuery = query(
        collection(db, 'volunteerLists'),
        where('tenantId', '==', currentUser.tenantId)
      );
      
      const snapshot = await getDocs(listsQuery);
      
      if (snapshot.empty) {
        console.log('No saved lists found for tenant:', currentUser.tenantId);
        setSavedLists([]);
        setError(null);
        return;
      }
      
      // Map the documents and handle timestamp conversion
      const lists = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Handle createdAt field - it might be a Firestore timestamp or already a date
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : 
                     data.createdAt?.seconds ? new Date(data.createdAt.seconds * 1000) : 
                     new Date()
        };
      });
      
      // Sort manually since we're not using orderBy in query
      lists.sort((a, b) => b.createdAt - a.createdAt);
      
      console.log(`Successfully loaded ${lists.length} saved lists:`, lists);
      setSavedLists(lists);
      setError(null);
      
    } catch (error) {
      console.error('Error loading saved lists:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      // More specific error handling
      if (error.code === 'permission-denied') {
        setError('Permission denied. Check your Firebase rules.');
      } else if (error.code === 'failed-precondition') {
        // This usually means an index is needed
        setError('Database configuration in progress. Please refresh in a moment.');
        // Try simpler query after delay
        setTimeout(() => {
          loadSavedListsSimple();
        }, 2000);
      } else {
        setError(`Failed to load saved lists: ${error.message}`);
      }
      setSavedLists([]);
    }
  };

  // Add this fallback function for simple query without ordering
  const loadSavedListsSimple = async () => {
    try {
      console.log('Attempting simple query without ordering...');
      const snapshot = await getDocs(collection(db, 'volunteerLists'));
      
      // Filter by tenantId manually
      const lists = [];
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.tenantId === currentUser?.tenantId) {
          lists.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date()
          });
        }
      });
      
      console.log(`Loaded ${lists.length} lists with simple query`);
      setSavedLists(lists);
      setError(null);
    } catch (error) {
      console.error('Simple query also failed:', error);
      setError('Unable to load saved lists');
    }
  };

  // Load volunteer details for a list
  const loadListMembers = async (list) => {
    if (!list.volunteerIds || list.volunteerIds.length === 0) {
      setListMembers([]);
      return;
    }

    try {
      setLoadingMembers(true);
      setListMembers([]);
      
      // Firebase limits 'in' queries to 10 items, so we need to batch
      const volunteerIds = list.volunteerIds;
      const allVolunteers = [];
      
      for (let i = 0; i < volunteerIds.length; i += 10) {
        const batch = volunteerIds.slice(i, i + 10);
        
        const volunteersQuery = query(
          collection(db, 'volunteers'),
          where(documentId(), 'in', batch)
        );
        
        const snapshot = await getDocs(volunteersQuery);
        const volunteers = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        allVolunteers.push(...volunteers);
      }
      
      setListMembers(allVolunteers);
      
    } catch (err) {
      console.error('Error loading list members:', err);
      setError('Failed to load volunteer details');
    } finally {
      setLoadingMembers(false);
    }
  };

  // View members handler
  const handleViewMembers = async (list) => {
    setViewingMembers(list);
    await loadListMembers(list);
  };

  // Load communication campaigns/history
  const loadCampaigns = async () => {
    try {
      console.log('Loading communication history...');
      console.log('Tenant ID:', currentUser?.tenantId);
      
      if (!currentUser?.tenantId) {
        console.error('No tenant ID available');
        setCampaigns([]);
        return;
      }
      
      // Get ALL communications first, then filter in JavaScript
      // This avoids the compound index requirement
      const snapshot = await getDocs(collection(db, 'communications'));
      
      console.log(`Total communications in collection: ${snapshot.size}`);
      
      // Filter for current tenant and map the data
      const campaignsData = [];
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        // Only include communications for current tenant
        if (data.tenantId === currentUser.tenantId) {
          campaignsData.push({
            id: doc.id,
            ...data,
            // Convert timestamp to Date if needed
            sentAt: data.sentAt ? new Date(data.sentAt) : 
                    data.actualSentAt ? new Date(data.actualSentAt) :
                    data.createdAt ? new Date(data.createdAt) :
                    new Date()
          });
        }
      });
      
      // Sort by sentAt date descending (newest first)
      campaignsData.sort((a, b) => {
        const dateA = a.sentAt instanceof Date ? a.sentAt : new Date(a.sentAt);
        const dateB = b.sentAt instanceof Date ? b.sentAt : new Date(b.sentAt);
        return dateB - dateA;
      });
      
      console.log(`✅ Loaded ${campaignsData.length} communications for this tenant`);
      setCampaigns(campaignsData);
      
    } catch (err) {
      console.error('❌ Error loading campaigns:', err);
      console.error('Error details:', err.code, err.message);
      setCampaigns([]);
      
      // Don't show error to user for loading issues
      // The UI will just show "No messages sent yet"
    }
  };

  // Send communication
  const handleSendMessage = async () => {
    if (!messageForm.subject || !messageForm.body || !selectedList) {
      setError('Please fill in all required fields and select a list');
      return;
    }

    // Validate email format if replyTo is provided
    if (messageForm.replyTo && !messageForm.replyTo.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError('Please enter a valid reply-to email address');
      return;
    }

    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      console.log('Starting to save communication...');
      console.log('Selected list:', selectedList);
      console.log('Current user:', currentUser);
      console.log('Reply-to email:', messageForm.replyTo || defaultReplyTo || 'none specified');
      
      // Create the communication data with regular Date objects
      const commData = {
        subject: messageForm.subject,
        body: messageForm.body,
        type: messageForm.type,
        listId: selectedList.id,
        listName: selectedList.name,
        volunteerIds: selectedList.volunteerIds || [],
        recipientCount: selectedList.volunteerIds?.length || selectedList.volunteerCount || 0,
        sentAt: new Date().toISOString(),
        sentBy: currentUser.email,
        tenantId: currentUser.tenantId,
        replyTo: messageForm.replyTo || defaultReplyTo || '', // Include reply-to
        status: 'pending', // IMPORTANT: Must be 'pending' for cloud function
        opens: 0,
        clicks: 0,
        createdAt: new Date().toISOString()
      };

      console.log('Communication data prepared:', commData);

      // Save to Firebase
      try {
        const docRef = await addDoc(collection(db, 'communications'), commData);
        console.log('✅ SUCCESS: Communication saved with ID:', docRef.id);
        
        // Update list usage
        try {
          await updateDoc(doc(db, 'volunteerLists', selectedList.id), {
            lastUsed: new Date().toISOString(),
            useCount: (selectedList.useCount || 0) + 1
          });
          console.log('✅ List usage updated');
        } catch (updateErr) {
          console.error('Warning: Failed to update list usage:', updateErr);
          // Don't fail the whole operation if this fails
        }

        // Reset form
        setMessageForm({
          subject: '',
          body: '',
          type: 'email',
          scheduledFor: null,
          selectedListId: null,
          replyTo: defaultReplyTo // Reset to default reply-to
        });
        setSelectedList(null);

        // Show success message
        alert(`Communication queued successfully!\n\nID: ${docRef.id}\nRecipients: ${commData.recipientCount}\nReply-To: ${commData.replyTo || 'System default'}\n\nThe cloud function will process this shortly.`);
        
        // Switch to history tab
        setActiveTab('history');
        
        // Reload campaigns after a delay
        setTimeout(() => {
          console.log('Reloading campaigns...');
          loadCampaigns();
          loadSavedLists();
        }, 3000);
        
      } catch (saveError) {
        console.error('❌ Failed to save to Firestore:', saveError);
        throw saveError;
      }
      
    } catch (err) {
      console.error('❌ Error in handleSendMessage:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);
      
      if (err.code === 'permission-denied') {
        setError('Permission denied. Check Firebase rules for communications collection.');
      } else if (err.code === 'unauthenticated') {
        setError('You must be logged in to send communications.');
      } else {
        setError(`Failed to save: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Delete a saved list
  const handleDeleteList = async (listId) => {
    if (!window.confirm('Are you sure you want to delete this list?')) return;
    
    try {
      await deleteDoc(doc(db, 'volunteerLists', listId));
      await loadSavedLists();
    } catch (err) {
      console.error('Error deleting list:', err);
      setError('Failed to delete list');
    }
  };

  // Duplicate a list
  const handleDuplicateList = async (list) => {
    try {
      const newList = {
        ...list,
        name: `${list.name} (Copy)`,
        createdAt: serverTimestamp(),
        createdBy: currentUser.email,
        useCount: 0,
        lastUsed: null
      };
      
      delete newList.id;
      
      await addDoc(collection(db, 'volunteerLists'), newList);
      await loadSavedLists();
      
      alert('List duplicated successfully!');
    } catch (err) {
      console.error('Error duplicating list:', err);
      setError('Failed to duplicate list');
    }
  };

  // Update list details
  const handleUpdateList = async () => {
    if (!editingList?.name) {
      setError('List name is required');
      return;
    }

    try {
      await updateDoc(doc(db, 'volunteerLists', editingList.id), {
        name: editingList.name,
        description: editingList.description,
        updatedAt: serverTimestamp()
      });
      
      await loadSavedLists();
      setShowEditModal(false);
      setEditingList(null);
    } catch (err) {
      console.error('Error updating list:', err);
      setError('Failed to update list');
    }
  };

  // Apply template
  const applyTemplate = (template) => {
    setMessageForm(prev => ({
      ...prev,
      subject: template.subject,
      body: template.body
    }));
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Calculate open rate
  const calculateOpenRate = (opens, recipients) => {
    if (!recipients || recipients === 0) return '0%';
    return `${Math.round((opens / recipients) * 100)}%`;
  };

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: '0 0 10px 0', fontSize: '28px', fontWeight: 'bold', color: '#333' }}>
          Communications Hub
        </h1>
        <p style={{ margin: 0, color: '#666', fontSize: '16px' }}>
          Manage volunteer outreach and track communication history
        </p>
      </div>

      {/* Navigation Tabs */}
      <div style={{ 
        display: 'flex', 
        borderBottom: '2px solid #dee2e6', 
        marginBottom: '30px',
        gap: '20px'
      }}>
        <button
          onClick={() => setActiveTab('lists')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'lists' ? '3px solid #007bff' : 'none',
            color: activeTab === 'lists' ? '#007bff' : '#666',
            fontSize: '16px',
            fontWeight: activeTab === 'lists' ? 'bold' : 'normal',
            cursor: 'pointer',
            marginBottom: '-2px'
          }}
        >
          📋 Saved Lists ({savedLists.length})
        </button>
        
        <button
          onClick={() => setActiveTab('compose')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'compose' ? '3px solid #007bff' : 'none',
            color: activeTab === 'compose' ? '#007bff' : '#666',
            fontSize: '16px',
            fontWeight: activeTab === 'compose' ? 'bold' : 'normal',
            cursor: 'pointer',
            marginBottom: '-2px'
          }}
        >
          ✉️ Compose Message
        </button>
        
        <button
          onClick={() => setActiveTab('history')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'history' ? '3px solid #007bff' : 'none',
            color: activeTab === 'history' ? '#007bff' : '#666',
            fontSize: '16px',
            fontWeight: activeTab === 'history' ? 'bold' : 'normal',
            cursor: 'pointer',
            marginBottom: '-2px'
          }}
        >
          📊 History ({campaigns.length})
        </button>
        
        <button
          onClick={() => setActiveTab('templates')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'templates' ? '3px solid #007bff' : 'none',
            color: activeTab === 'templates' ? '#007bff' : '#666',
            fontSize: '16px',
            fontWeight: activeTab === 'templates' ? 'bold' : 'normal',
            cursor: 'pointer',
            marginBottom: '-2px'
          }}
        >
          📝 Templates
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{ 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          border: '1px solid #f5c6cb'
        }}>
          {error}
          <button 
            onClick={() => setError('')}
            style={{ float: 'right', background: 'none', border: 'none', color: '#721c24', cursor: 'pointer' }}
          >
            ×
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          Loading...
        </div>
      )}

      {/* Saved Lists Tab */}
      {activeTab === 'lists' && !loading && (
        <div>
          <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, fontSize: '20px', color: '#333' }}>Your Saved Lists</h2>
            <button
              onClick={() => window.location.href = '/volunteers'}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>➕</span>
              Create New List from Directory
            </button>
          </div>

          {savedLists.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px', 
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>📋</div>
              <h3 style={{ color: '#666', marginBottom: '10px' }}>No saved lists yet</h3>
              <p style={{ color: '#999', marginBottom: '20px' }}>
                Create lists from the Volunteer Directory to organize your outreach
              </p>
              <button
                onClick={() => window.location.href = '/volunteers'}
                style={{
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                Go to Directory
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
              {savedLists.map(list => (
                <div key={list.id} style={{ 
                  backgroundColor: 'white', 
                  borderRadius: '8px', 
                  border: '1px solid #dee2e6',
                  padding: '20px',
                  transition: 'box-shadow 0.2s'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                    <div>
                      <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', color: '#333' }}>
                        {list.name}
                      </h3>
                      <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#666' }}>
                        {list.description || 'No description'}
                      </p>
                    </div>
                    <span style={{
                      padding: '4px 8px',
                      backgroundColor: '#e9ecef',
                      borderRadius: '12px',
                      fontSize: '12px',
                      color: '#495057',
                      fontWeight: 'bold'
                    }}>
                      {list.volunteerIds?.length || list.volunteerCount || 0} volunteers
                    </span>
                  </div>

                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                    <div>📅 Created: {formatDate(list.createdAt)}</div>
                    <div>✉️ Last used: {formatDate(list.lastUsed)}</div>
                    <div>📊 Used {list.useCount || 0} times</div>
                    {list.filters && (
                      <div style={{ marginTop: '8px' }}>
                        <strong>Filters:</strong>
                        <div style={{ fontSize: '12px', marginTop: '4px' }}>
                          {list.filters.skills?.length > 0 && <div>Skills: {list.filters.skills.join(', ')}</div>}
                          {list.filters.status && <div>Status: {list.filters.status}</div>}
                          {list.filters.searchTerm && <div>Search: {list.filters.searchTerm}</div>}
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handleViewMembers(list)}
                      style={{
                        backgroundColor: '#6f42c1',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '13px',
                        cursor: 'pointer'
                      }}
                    >
                      👥 View Members
                    </button>
                    
                    <button
                      onClick={() => {
                        setSelectedList(list);
                        setActiveTab('compose');
                      }}
                      style={{
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '13px',
                        cursor: 'pointer'
                      }}
                    >
                      Send Message
                    </button>
                    
                    <button
                      onClick={() => {
                        setEditingList(list);
                        setShowEditModal(true);
                      }}
                      style={{
                        backgroundColor: '#ffc107',
                        color: '#212529',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '13px',
                        cursor: 'pointer'
                      }}
                    >
                      Edit
                    </button>
                    
                    <button
                      onClick={() => handleDuplicateList(list)}
                      style={{
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '13px',
                        cursor: 'pointer'
                      }}
                    >
                      Duplicate
                    </button>
                    
                    <button
                      onClick={() => handleDeleteList(list.id)}
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '13px',
                        cursor: 'pointer'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Compose Message Tab */}
      {activeTab === 'compose' && !loading && (
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 style={{ marginBottom: '20px', fontSize: '20px', color: '#333' }}>
            Compose New Message
          </h2>

          {/* List Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
              Select Recipients List *
            </label>
            {selectedList ? (
              <div style={{
                backgroundColor: '#e3f2fd',
                padding: '12px',
                borderRadius: '6px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <strong>{selectedList.name}</strong>
                  <span style={{ marginLeft: '10px', color: '#666' }}>
                    ({selectedList.volunteerIds?.length || selectedList.volunteerCount || 0} recipients)
                  </span>
                </div>
                <button
                  onClick={() => setSelectedList(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#1976d2',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Change
                </button>
              </div>
            ) : (
              <select
                value={messageForm.selectedListId || ''}
                onChange={(e) => {
                  const list = savedLists.find(l => l.id === e.target.value);
                  setSelectedList(list);
                  setMessageForm(prev => ({ ...prev, selectedListId: e.target.value }));
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              >
                <option value="">Choose a list...</option>
                {savedLists.map(list => (
                  <option key={list.id} value={list.id}>
                    {list.name} ({list.volunteerIds?.length || list.volunteerCount || 0} volunteers)
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Message Type */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
              Message Type
            </label>
            <div style={{ display: 'flex', gap: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  value="email"
                  checked={messageForm.type === 'email'}
                  onChange={(e) => setMessageForm(prev => ({ ...prev, type: e.target.value }))}
                  style={{ marginRight: '8px' }}
                />
                📧 Email
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  value="sms"
                  checked={messageForm.type === 'sms'}
                  onChange={(e) => setMessageForm(prev => ({ ...prev, type: e.target.value }))}
                  style={{ marginRight: '8px' }}
                />
                📱 SMS
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  value="both"
                  checked={messageForm.type === 'both'}
                  onChange={(e) => setMessageForm(prev => ({ ...prev, type: e.target.value }))}
                  style={{ marginRight: '8px' }}
                />
                📧📱 Both
              </label>
            </div>
          </div>

          {/* Reply-To Email Field */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
              Reply-To Email Address
            </label>
            <input
              type="email"
              value={messageForm.replyTo}
              onChange={(e) => setMessageForm(prev => ({ ...prev, replyTo: e.target.value }))}
              placeholder={defaultReplyTo ? `Default: ${defaultReplyTo}` : "Enter reply-to email..."}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px'
              }}
            />
            <div style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
              Recipients will reply to this email address. 
              {defaultReplyTo && ` Leave blank to use system default (${defaultReplyTo})`}
            </div>
          </div>

          {/* Subject Line */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
              Subject Line *
            </label>
            <input
              type="text"
              value={messageForm.subject}
              onChange={(e) => setMessageForm(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="Enter your subject line..."
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px'
              }}
            />
            <div style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
              Tip: Use merge tags like {'{{firstName}}'}, {'{{eventName}}'}, {'{{eventDate}}'}
            </div>
          </div>

          {/* Message Body */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
              Message Body *
            </label>
            <textarea
              value={messageForm.body}
              onChange={(e) => setMessageForm(prev => ({ ...prev, body: e.target.value }))}
              placeholder="Type your message here..."
              style={{
                width: '100%',
                minHeight: '200px',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px',
                resize: 'vertical'
              }}
            />
            <div style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
              {messageForm.body.length} characters
              {messageForm.type === 'sms' && messageForm.body.length > 160 && 
                <span style={{ color: '#dc3545' }}> (Warning: SMS will be split into multiple messages)</span>
              }
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                setMessageForm({
                  subject: '',
                  body: '',
                  type: 'email',
                  scheduledFor: null,
                  selectedListId: null,
                  replyTo: defaultReplyTo
                });
                setSelectedList(null);
              }}
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '4px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
            
            <button
              onClick={handleSendMessage}
              disabled={!messageForm.subject || !messageForm.body || !selectedList}
              style={{
                backgroundColor: (!messageForm.subject || !messageForm.body || !selectedList) ? '#ccc' : '#28a745',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '4px',
                fontSize: '16px',
                cursor: (!messageForm.subject || !messageForm.body || !selectedList) ? 'not-allowed' : 'pointer'
              }}
            >
              Send Now
            </button>
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && !loading && (
        <div>
          <h2 style={{ marginBottom: '20px', fontSize: '20px', color: '#333' }}>
            Communication History
          </h2>

          {campaigns.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px', 
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>📭</div>
              <h3 style={{ color: '#666', marginBottom: '10px' }}>No messages sent yet</h3>
              <p style={{ color: '#999' }}>
                Your communication history will appear here
              </p>
            </div>
          ) : (
            <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Subject</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>List</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Recipients</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Type</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Sent</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map(campaign => (
                    <tr key={campaign.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                      <td style={{ padding: '12px' }}>
                        <div style={{ fontWeight: 'bold' }}>{campaign.subject}</div>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                          {campaign.body.substring(0, 50)}...
                        </div>
                      </td>
                      <td style={{ padding: '12px' }}>{campaign.listName}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {campaign.sentCount !== undefined ? `${campaign.sentCount}/${campaign.recipientCount}` : campaign.recipientCount}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {campaign.type === 'email' && '📧'}
                        {campaign.type === 'sms' && '📱'}
                        {campaign.type === 'both' && '📧📱'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span style={{
                          padding: '2px 8px',
                          backgroundColor: campaign.status === 'sent' ? '#d4edda' : 
                                         campaign.status === 'pending' ? '#fff3cd' : 
                                         campaign.status === 'failed' ? '#f8d7da' : '#e9ecef',
                          color: campaign.status === 'sent' ? '#155724' : 
                                campaign.status === 'pending' ? '#856404' : 
                                campaign.status === 'failed' ? '#721c24' : '#495057',
                          borderRadius: '12px',
                          fontSize: '12px'
                        }}>
                          {campaign.status || 'unknown'}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div>{formatDate(campaign.sentAt)}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>by {campaign.sentBy}</div>
                        {campaign.replyTo && (
                          <div style={{ fontSize: '11px', color: '#999' }}>Reply-to: {campaign.replyTo}</div>
                        )}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <button
                          onClick={() => {
                            setMessageForm({
                              subject: campaign.subject,
                              body: campaign.body,
                              type: campaign.type,
                              scheduledFor: null,
                              selectedListId: campaign.listId,
                              replyTo: campaign.replyTo || defaultReplyTo
                            });
                            setActiveTab('compose');
                          }}
                          style={{
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          Resend
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && !loading && (
        <div>
          <h2 style={{ marginBottom: '20px', fontSize: '20px', color: '#333' }}>
            Message Templates
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
            {templates.map(template => (
              <div key={template.id} style={{ 
                backgroundColor: 'white', 
                borderRadius: '8px', 
                border: '1px solid #dee2e6',
                padding: '20px'
              }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#333' }}>
                  {template.name}
                </h3>
                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ fontSize: '14px', color: '#666' }}>Subject:</strong>
                  <div style={{ fontSize: '14px', marginTop: '4px' }}>{template.subject}</div>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <strong style={{ fontSize: '14px', color: '#666' }}>Body:</strong>
                  <div style={{ 
                    fontSize: '13px', 
                    marginTop: '4px',
                    backgroundColor: '#f8f9fa',
                    padding: '10px',
                    borderRadius: '4px',
                    whiteSpace: 'pre-wrap',
                    maxHeight: '150px',
                    overflow: 'auto'
                  }}>
                    {template.body}
                  </div>
                </div>
                <button
                  onClick={() => {
                    applyTemplate(template);
                    setActiveTab('compose');
                  }}
                  style={{
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    width: '100%'
                  }}
                >
                  Use This Template
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* View Members Modal - stays the same */}
      {viewingMembers && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '24px', color: '#333' }}>
                {viewingMembers.name} - Members
              </h2>
              <button
                onClick={() => {
                  setViewingMembers(null);
                  setListMembers([]);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#999'
                }}
              >
                ×
              </button>
            </div>
            
            {loadingMembers ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                Loading volunteer details...
              </div>
            ) : listMembers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                No volunteer details found for this list.
                <br />
                <small>Some volunteers may have been removed from the system.</small>
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: '15px', color: '#666' }}>
                  {listMembers.length} volunteer{listMembers.length !== 1 ? 's' : ''} in this list
                </div>
                <div style={{ 
                  backgroundColor: 'white', 
                  borderRadius: '8px', 
                  border: '1px solid #dee2e6',
                  overflow: 'hidden'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa' }}>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Name</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Email</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Phone</th>
                        <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Status</th>
                        <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listMembers.map(volunteer => (
                        <tr key={volunteer.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                          <td style={{ padding: '12px' }}>
                            <strong>{volunteer.firstName} {volunteer.lastName}</strong>
                          </td>
                          <td style={{ padding: '12px', fontSize: '14px' }}>
                            {volunteer.email}
                          </td>
                          <td style={{ padding: '12px', fontSize: '14px' }}>
                            {volunteer.phone || 'No phone'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              backgroundColor: volunteer.status === 'active' ? '#d4edda' : 
                                             volunteer.status === 'inactive' ? '#f8d7da' : '#fff3cd',
                              color: volunteer.status === 'active' ? '#155724' : 
                                     volunteer.status === 'inactive' ? '#721c24' : '#856404'
                            }}>
                              {volunteer.status || 'unknown'}
                            </span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', fontSize: '14px' }}>
                            {volunteer.totalHours || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div style={{ marginTop: '20px', textAlign: 'right' }}>
                  <button
                    onClick={() => {
                      setSelectedList(viewingMembers);
                      setViewingMembers(null);
                      setListMembers([]);
                      setActiveTab('compose');
                    }}
                    style={{
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '4px',
                      fontSize: '16px',
                      cursor: 'pointer',
                      marginRight: '10px'
                    }}
                  >
                    Send Message to This List
                  </button>
                  <button
                    onClick={() => {
                      setViewingMembers(null);
                      setListMembers([]);
                    }}
                    style={{
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '4px',
                      fontSize: '16px',
                      cursor: 'pointer'
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit List Modal - stays the same */}
      {showEditModal && editingList && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            width: '90%',
            maxWidth: '500px'
          }}>
            <h2 style={{ margin: '0 0 20px 0', fontSize: '24px', color: '#333' }}>
              Edit List
            </h2>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                List Name *
              </label>
              <input
                type="text"
                value={editingList.name}
                onChange={(e) => setEditingList(prev => ({ ...prev, name: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Description
              </label>
              <textarea
                value={editingList.description || ''}
                onChange={(e) => setEditingList(prev => ({ ...prev, description: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px',
                  minHeight: '80px',
                  resize: 'vertical'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingList(null);
                }}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateList}
                style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Communications;